import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getElementsById,
  getElementsEventDataById,
} from "core-integration/src/store/elements/reducers";
import { fetchEventLive } from "core-integration/src/store/elements/thunks";
import {
  getEntry,
  getEntryEventFormation,
  getEntryEventPicks,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryEventPicks,
  fetchEntryPhaseStandings,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import { IPickLight } from "core-integration/src/store/entries/types";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getFixturesForEventById } from "core-integration/src/store/fixtures/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IRules } from "core-integration/src/store/game/types";
import { getPhases } from "core-integration/src/store/phases/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import range from "lodash/range";
import React, { useCallback, useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import Entry from "../Entry";
import EntryEventScoreboard from "../EntryEventScoreboard";
import Fixtures from "../Fixtures";
import { Main, Secondary, Wrapper } from "../Layout";
import Link from "../Link";
import { NotFound } from "../Routes";
import Table from "../Table";
import Title from "../Title";
import { VisuallyHidden } from "../Utils";
import DreamTeam from "../icons/DreamTeam";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import BenchUnit from "./BenchUnit";
import ElementExplainDialog from "./ElementExplainDialog";
import EntryEventTable from "./EntryEventTable";
import PitchFormation from "./PitchFormation";
import TeamElementGroup from "./TeamElementGroup";
import { BenchElements } from "./styles";
import valueForPlayerElement from "./valueForPlayedElement";

type Props = WithTranslation;

const EntryEvent: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const params = useParams();
  const [pickForMenu, setPickForMenu] = useState<IPickLight | null>(null);
  const entryId = Number(params.entryId);
  const eventId = Number(params.eventId);
  const entry = useSelector((state: RootState) => getEntry(state, entryId));
  const phases = useSelector(getPhases);

  const activePhase = phases.find(
    (p) =>
      p.id > 1 &&
      p.start_event <= Number(eventId) &&
      p.stop_event >= Number(eventId),
  );

  const elementsById = useSelector(getElementsById);
  const elementsDataById = useSelector((state: RootState) =>
    getElementsEventDataById(state, eventId),
  );

  const event = useSelector(
    (state: RootState) => getEventsById(state)[eventId],
  );
  const now = useSelector((state: RootState) => getCurrentEvent(state));
  const entryData = useSelector((state: RootState) =>
    getEntryEventPicks(state, entryId, eventId),
  );
  const fixturesById = useSelector((state: RootState) =>
    getFixturesForEventById(state, eventId),
  );
  const formation = useSelector((state: RootState) =>
    getEntryEventFormation(state, entryId, eventId),
  );
  const rules = useSelector(getRules) as IRules;
  const teamsById = useSelector(getTeamsById);

  const handleShowMenuForPickElement = (element: number) => {
    const matches = entryData!.picks.filter((p) => p.element === element);
    if (matches.length) {
      setPickForMenu(matches[0]);
    }
  };

  const handleHideMenuForPick = () => {
    setPickForMenu(null);
  };

  const renderDreamTeam = (pick: IPickLight) =>
    elementsDataById &&
    elementsDataById[pick.element] &&
    elementsDataById[pick.element].stats.in_dreamteam ? (
      <Link to={`/dream-team/${event!.id}`}>
        <DreamTeam />
      </Link>
    ) : null;

  const fetchData = useCallback(() => {
    dispatch(fetchEntrySummary(Number(entryId)));
    dispatch(fetchEntryEventPicks(Number(entryId), Number(eventId)));
    dispatch(fetchEventLive(Number(eventId)));
    if (eventId && entryId) {
      dispatch(fetchEntryPhaseStandings(Number(entryId), activePhase!.id));
    }
  }, [activePhase, dispatch, entryId, eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle unknown and unstarted events
  if (!now || !event || event.id > now.id) {
    return <NotFound />;
  }
  if (!entry || !entryData) {
    return null;
  }

  // Create a new function on each render as data could have changed and
  // need to ensure a render of connected subcomponents
  const renderPickValue = valueForPlayerElement({
    elementsById,
    elementsDataById,
    fixturesById,
    teamsById,
  });
  const benchHeading: Record<string, React.ReactNode> = {
    11: (
      <abbr title={t<string>("entryEvent.goalkeeper.title", "Goalkeeper")}>
        {t("entryEvent.goalkeeper.abbr", "GK")}
      </abbr>
    ),
    12: "1",
    13: "2",
    14: "3",
  };
  // Handle events before the entry started
  return (
    <Wrapper>
      <Main>
        <VisuallyHidden as="div">
          <h2>
            {t("entryEvent.title", "Points")} - {entry.name}
          </h2>
        </VisuallyHidden>
        <EntryEventScoreboard />

        {/* TEAM */}
        <Tabs centered>
          <TabPanel
            label={t("entryEvent.pitchView", "Court View")}
            link="pitch"
          >
            <Box mb="4px">
              <TeamElementGroup
                title={t("myTeam.startersN", "Starting 5")}
                isHiddenTitle={false}
              >
                <PitchFormation
                  showUpcomingFixtures={false}
                  chipName={entryData.active_chip}
                  formation={formation}
                  picks={entryData.picks}
                  renderDreamTeam={renderDreamTeam}
                  renderElementMenu={handleShowMenuForPickElement}
                  renderPickValue={renderPickValue}
                />
              </TeamElementGroup>
            </Box>
            <Box mb={4}>
              <TeamElementGroup
                title={t("myTeam.substitutesn", "Bench")}
                isBench={true}
              >
                <BenchElements>
                  {range(rules.squad_squadplay, rules.squad_squadsize).map(
                    (i) => (
                      <BenchUnit
                        key={i}
                        chipName={entryData.active_chip}
                        heading={benchHeading[i]}
                        pick={entryData.picks[i]}
                        renderDreamTeam={renderDreamTeam}
                        renderElementMenu={() =>
                          handleShowMenuForPickElement(
                            entryData.picks[i].element,
                          )
                        }
                        renderPickValue={renderPickValue}
                        showUpcomingFixtures={false}
                      />
                    ),
                  )}
                </BenchElements>
              </TeamElementGroup>
            </Box>
          </TabPanel>
          <TabPanel label={t("entryEvent.listView", "List View")} link="list">
            <Box my={4} bg="white">
              <EntryEventTable
                title={t("entryEvent.starters", "Starters")}
                picks={entryData.picks.slice(0, rules.squad_squadplay)}
                renderElementMenu={handleShowMenuForPickElement}
                chipName={entryData.active_chip}
                dataById={elementsDataById}
              />
              <EntryEventTable
                title={t("entryEvent.substitutesn", "Bench")}
                picks={entryData.picks.slice(rules.squad_squadplay)}
                renderElementMenu={handleShowMenuForPickElement}
                chipName={entryData.active_chip}
                dataById={elementsDataById}
              />
            </Box>
          </TabPanel>
        </Tabs>

        {entryData.automatic_subs.length > 0 && (
          <>
            <Box mx={2}>
              <Title>
                {t("entryEvent.autoSubs.title", "Automatic Substitutions")}
              </Title>
            </Box>
            <Table>
              <thead>
                <tr>
                  <th>{t("entryEvent.autoSubs.playerOut", "Player out")}</th>
                  <th>{t("entryEvent.autoSubs.playerIn", "Player in")}</th>
                </tr>
              </thead>
              <tbody>
                {entryData.automatic_subs.map((s) => (
                  <tr key={s.element_out}>
                    <td>{elementsById[s.element_out].web_name}</td>
                    <td>{elementsById[s.element_in].web_name}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
        {pickForMenu && (
          <ElementExplainDialog
            elementId={pickForMenu.element}
            eventId={event.id}
            closeDialog={handleHideMenuForPick}
          />
        )}
        <Fixtures eventId={event.id} />
      </Main>
      <Secondary>
        <Entry entryId={entry.id} />
      </Secondary>
    </Wrapper>
  );
};

export default withTranslation()(EntryEvent);
